import { useState } from 'react';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  TYPE_LABEL,
  type RequestType,
  type SupportRequest,
} from '../../services/supportRequest.service';
import {
  useDeleteSupportRequest,
  useRequestPermissions,
  useUpdateSupportRequest,
} from '../../hooks/useSupportRequests';
import { RequestSubscribers } from './RequestSubscribers';

const TYPE_OPTIONS: RequestType[] = [
  'data_source',
  'data_quality',
  'question',
  'change',
  'other',
];

/**
 * Edit / delete controls for the request header.
 *
 * Visibility comes from the server's own permission check rather than being
 * re-derived from roles here, so the button that appears and the rule the API
 * enforces can't drift. Staff, tenant admins and the person who opened the
 * request all qualify — a wider gate than the triage controls, since the title
 * and the request's existence belong to whoever raised it too.
 */
export function ManageRequest({ request }: { request: SupportRequest }) {
  const { data: permissions } = useRequestPermissions(request.id);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!permissions?.can_manage) return null;

  return (
    <>
      <RequestSubscribers request={request} />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setEditOpen(true)}
        title="Edit request"
        aria-label="Edit request"
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={() => setDeleteOpen(true)}
        title="Delete request"
        aria-label="Delete request"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      {/* Mounted only while open so the form's useState initializers reseed
          from the request each time — no effect, and no stale draft surviving
          a cancelled edit. */}
      {editOpen && (
        <EditRequestDialog request={request} onOpenChange={setEditOpen} />
      )}
      {deleteOpen && (
        <DeleteRequestDialog request={request} onOpenChange={setDeleteOpen} />
      )}
    </>
  );
}

function EditRequestDialog({
  request,
  onOpenChange,
}: {
  request: SupportRequest;
  onOpenChange: (v: boolean) => void;
}) {
  const [title, setTitle] = useState(request.title);
  const [type, setType] = useState<RequestType>(request.request_type);
  const [description, setDescription] = useState(request.description ?? '');
  const updateRequest = useUpdateSupportRequest();

  const canSave = title.trim().length > 2 && !updateRequest.isPending;

  const save = async () => {
    if (!canSave) return;
    await updateRequest.mutateAsync({
      id: request.id,
      patch: {
        title: title.trim(),
        request_type: type,
        description: description.trim(),
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit request</DialogTitle>
          <DialogDescription>
            Everyone on the request sees these changes.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-request-title">Title</Label>
            <Input
              id="edit-request-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-request-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as RequestType)}>
              <SelectTrigger id="edit-request-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-request-description">Details</Label>
            <Textarea
              id="edit-request-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            {updateRequest.isPending && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRequestDialog({
  request,
  onOpenChange,
}: {
  request: SupportRequest;
  onOpenChange: (v: boolean) => void;
}) {
  const deleteRequest = useDeleteSupportRequest();

  const confirm = async () => {
    await deleteRequest.mutateAsync(request.id);
    onOpenChange(false);
    // Full reload rather than a router navigate: the request this page is built
    // around no longer exists, and any in-flight query for it would 404.
    window.location.assign('/requests');
  };

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this request?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium text-foreground">{request.title}</span>{' '}
            will be removed for everyone on it, along with its conversation and
            files. Pliable keeps the record internally, but no one on the request
            will be able to reach it and you can't undo this yourself.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteRequest.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog up while the delete is in flight, so the button
              // can show progress instead of vanishing mid-request.
              e.preventDefault();
              confirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteRequest.isPending && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            Delete request
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
